import { useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { $accountByIdStore, $generateAccountConfirmationLetterStore } from '@lib/store';
import { useAccountFlow } from '@shared/hooks';
import { FileUtils } from '@shared/utils';
import { Item } from './item';
import { AccountDetailsModal } from './accountDetailsModal';
import { Button, Icon, Spinner } from '@shared/widgets';
import ShareIcon from '@assets/icons/share2.svg';

export interface IForm {
  accountLabel: string;
  costCentre: string;
}

export const AccountDetails = () => {
  const { data } = useUnit($accountByIdStore.store);
  const generateAccountConfirmationLetterStore = useUnit($generateAccountConfirmationLetterStore.store);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { t } = useTranslation('account');

  const methods = useForm<IForm>({ defaultValues: { accountLabel: data?.accountLabel ?? '', costCentre: data?.costCentre ?? '' } });
  const accountLabel = methods.watch('accountLabel');
  const costCentre = methods.watch('costCentre');

  const submitHandler = async ({ accountLabel, costCentre }: IForm) => {
    if (!data?.id) return;
    await $accountByIdStore.events.updateDetails({ accountId: data.id, label: accountLabel, costCentre, category: data.category! });
  };

  const generateAccountConfirmationLetterHandler = async () => {
    if (!data?.id) return;
    const result = await $generateAccountConfirmationLetterStore.events.generateAccountConfirmationLetter({ id: data.id, documentType: 'Pdf' });
    if (result) FileUtils.openFile(result);
  };

  const accountFlow = useAccountFlow(data?.id);
  const requisites = useMemo(
    () => [
      { name: t('details.fields.accountLabel'), value: data?.accountLabel || '-' },
      { name: t('details.fields.costCentre'), value: data?.costCentre || '-' },
      { name: t('details.fields.beneficiary'), value: data?.beneficiaryName || '-' },
      { name: t('details.fields.iban'), value: data?.iban || '-' },
      { name: t('details.fields.bicSwiftCode'), value: data?.bicOrSwiftCode || '-' },
      { name: t('details.fields.correspondingBankBic'), value: data?.corrBank || '-' },
      { name: t('details.fields.bankNameAndAddress'), value: `${data?.bankName} ${data?.bankAddress}` || '-' },
    ],
    [t, data],
  );
  const canEditLabel = !!accountFlow?.editAccountLabel;
  const canEditCost = !!accountFlow?.editCostCentre;
  return (
    <div className='flex flex-col gap-4'>
      <Spinner show={generateAccountConfirmationLetterStore.loading} position />
      <FormProvider {...methods}>
        <form className='flex flex-col gap-1' noValidate onSubmit={methods.handleSubmit(submitHandler)}>
          {requisites?.map((requisite, idx) => (
            <Item
              key={idx}
              name={requisite.name}
              value={requisite.value}
              isEditableLabel={canEditLabel && idx === 0}
              isEditableCost={canEditCost && idx === 1}
              isCopy={(idx === 0 && !canEditLabel) || (idx === 1 && !canEditCost) || idx >= 2}
            />
          ))}

          {((data?.accountLabel ?? '') !== accountLabel || (data?.costCentre ?? '') !== costCentre) && (
            <Button className='mt-4' fullWidth color='primary' type='submit' disabled={methods.formState.isSubmitting}>
              {t('buttons.saveChanges', { ns: 'common' })}
            </Button>
          )}
        </form>
      </FormProvider>
      <Button fullWidth color='outlined' onClick={() => setIsModalOpen(true)}>
        <Icon icon={<ShareIcon />} color='inherit' />
        {t('details.buttons.shareAccountDetails')}
      </Button>
      <Button color='outlined' fullWidth onClick={generateAccountConfirmationLetterHandler}>
        Account Confirmation Letter
      </Button>
      <AccountDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} values={requisites} />
    </div>
  );
};
